import React, { useState, useEffect } from 'react';
import {
  Shield,
  Lock,
  Key,
  FileText,
  Download,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Smartphone,
  Eye,
  EyeOff,
  RefreshCw,
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  FileJson,
  User,
  Building,
  Brain
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// API functions
const securityApi = {
  get2FAStatus: async (token) => {
    const res = await fetch(`${API_URL}/api/security/2fa/status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.json();
  },
  
  setup2FA: async (token, password) => {
    const res = await fetch(`${API_URL}/api/security/2fa/setup`, {
      method: 'POST',
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ password })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to setup 2FA');
    }
    return res.json();
  },
  
  verifySetup2FA: async (token, code) => {
    const res = await fetch(`${API_URL}/api/security/2fa/verify-setup`, {
      method: 'POST',
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ code })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Verification failed');
    }
    return res.json();
  },
  
  disable2FA: async (token, password, code) => {
    const res = await fetch(`${API_URL}/api/security/2fa/disable`, {
      method: 'POST',
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ password, code })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to disable 2FA');
    }
    return res.json();
  },
  
  regenerateBackupCodes: async (token, code) => {
    const res = await fetch(`${API_URL}/api/security/2fa/regenerate-backup-codes`, {
      method: 'POST',
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ code })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to regenerate codes');
    }
    return res.json();
  },
  
  getComplianceReport: async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/security/compliance-report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        return null;
      }
      return await res.json();
    } catch (error) {
      console.error('Compliance report fetch error:', error);
      return null;
    }
  },
  
  getAITrainingConsent: async (token) => {
    const res = await fetch(`${API_URL}/api/security/ai-training-consent`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.json();
  },
  
  updateAITrainingConsent: async (token, optOut) => {
    const res = await fetch(`${API_URL}/api/security/ai-training-consent`, {
      method: 'PUT',
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ opt_out: optOut })
    });
    return res.json();
  },
  
  exportAuditLogs: async (token, format = 'csv') => {
    const res = await fetch(`${API_URL}/api/security/audit-logs/export?format=${format}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res;
  },
  
  exportUserData: async (token, userId) => {
    const res = await fetch(`${API_URL}/api/security/data-export/${userId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res;
  }
};

export default function SecuritySettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tfaStatus, setTfaStatus] = useState(null);
  const [complianceReport, setComplianceReport] = useState(null);
  const [aiConsent, setAiConsent] = useState(null);
  
  // 2FA dialogs
  const [showSetup2FA, setShowSetup2FA] = useState(false);
  const [showDisable2FA, setShowDisable2FA] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [showRegenerateBackup, setShowRegenerateBackup] = useState(false);
  
  // 2FA setup state
  const [setupStep, setSetupStep] = useState(1);
  const [setupData, setSetupData] = useState(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [copiedCode, setCopiedCode] = useState(false);
  
  // Compliance sections
  const [expandedSections, setExpandedSections] = useState({
    encryption: true,
    authentication: true,
    accessControl: false,
    audit: false,
    privacy: false
  });
  
  const token = localStorage.getItem('saas_token');
  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.is_admin;

  useEffect(() => {
    const token = localStorage.getItem('saas_token');
    const userIsAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.is_admin;
    
    const loadData = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        // Load data one by one to handle partial failures
        try {
          const tfaRes = await securityApi.get2FAStatus(token);
          if (tfaRes && !tfaRes.detail) setTfaStatus(tfaRes);
        } catch (e) {
          console.warn('2FA status load failed:', e);
        }

        try {
          const reportRes = await securityApi.getComplianceReport(token);
          if (reportRes && !reportRes.detail) {
            setComplianceReport(reportRes);
          }
        } catch (e) {
          console.warn('Compliance report load failed:', e);
        }

        if (userIsAdmin) {
          try {
            const consentRes = await securityApi.getAITrainingConsent(token);
            if (consentRes && !consentRes.detail) setAiConsent(consentRes);
          } catch (e) {
            console.warn('AI consent load failed:', e);
          }
        }
      } catch (err) {
        console.error('Error loading security data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [user]);

  const loadSecurityData = async () => {
    setLoading(true);
    try {
      // Load data one by one to handle partial failures
      try {
        const tfaRes = await securityApi.get2FAStatus(token);
        if (tfaRes && !tfaRes.detail) setTfaStatus(tfaRes);
      } catch (e) {
        console.warn('2FA status load failed:', e);
      }

      try {
        const reportRes = await securityApi.getComplianceReport(token);
        if (reportRes && !reportRes.detail && reportRes.security_controls) {
          setComplianceReport(reportRes);
        }
      } catch (e) {
        console.warn('Compliance report load failed:', e);
      }

      if (isAdmin) {
        try {
          const consentRes = await securityApi.getAITrainingConsent(token);
          if (consentRes && !consentRes.detail) setAiConsent(consentRes);
        } catch (e) {
          console.warn('AI consent load failed:', e);
        }
      }
    } catch (err) {
      console.error('Error loading security data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    try {
      const result = await securityApi.setup2FA(token, password);
      setSetupData(result);
      setSetupStep(2);
      setPassword('');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleVerify2FA = async () => {
    try {
      const result = await securityApi.verifySetup2FA(token, verifyCode);
      setBackupCodes(result.backup_codes);
      setSetupStep(3);
      setVerifyCode('');
      toast.success('2FA enabled successfully!');
      loadSecurityData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDisable2FA = async () => {
    try {
      await securityApi.disable2FA(token, password, verifyCode);
      setShowDisable2FA(false);
      setPassword('');
      setVerifyCode('');
      toast.success('2FA disabled');
      loadSecurityData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRegenerateBackup = async () => {
    try {
      const result = await securityApi.regenerateBackupCodes(token, verifyCode);
      setBackupCodes(result.backup_codes);
      setShowRegenerateBackup(false);
      setShowBackupCodes(true);
      setVerifyCode('');
      toast.success('Backup codes regenerated');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAIConsentToggle = async () => {
    try {
      const newValue = !aiConsent?.ai_training_opt_out;
      await securityApi.updateAITrainingConsent(token, newValue);
      setAiConsent(prev => ({ ...prev, ai_training_opt_out: newValue }));
      toast.success(newValue ? 'AI training opt-out enabled' : 'AI training opt-out disabled');
    } catch (err) {
      toast.error('Failed to update AI training preference');
    }
  };

  const handleExportAuditLogs = async (format) => {
    try {
      const res = await securityApi.exportAuditLogs(token, format);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Audit logs exported');
    } catch (err) {
      toast.error('Failed to export audit logs');
    }
  };

  const handleExportMyData = async () => {
    try {
      const res = await securityApi.exportUserData(token, user.id);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my_data_export.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Your data exported');
    } catch (err) {
      toast.error('Failed to export data');
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
    toast.success('Backup codes copied');
  };

  const getStatusBadge = (status) => {
    if (!status) {
      return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-400"><XCircle className="h-3 w-3" /> Unknown</span>;
    }
    if (status === 'ENABLED' || status === 'IMPLEMENTED' || status === 'CONTROLS_IMPLEMENTED' || status === 'CONTROLS_IN_PLACE') {
      return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400"><CheckCircle className="h-3 w-3" /> {status.replace(/_/g, ' ')}</span>;
    } else if (status === 'AVAILABLE') {
      return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"><CheckCircle className="h-3 w-3" /> Available</span>;
    } else if (status === 'PARTIAL' || status === 'NOT_SET') {
      return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"><AlertTriangle className="h-3 w-3" /> {status.replace(/_/g, ' ')}</span>;
    } else {
      return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-400"><XCircle className="h-3 w-3" /> {status.replace(/_/g, ' ')}</span>;
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Two-Factor Authentication */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-teal-600" />
            Two-Factor Authentication (2FA)
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium">2FA Status:</span>
                {tfaStatus?.enabled ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                    <CheckCircle className="h-3 w-3" /> Enabled
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400">
                    <XCircle className="h-3 w-3" /> Not Enabled
                  </span>
                )}
              </div>
              {tfaStatus?.enabled && (
                <p className="text-sm text-slate-500 mt-1">
                  {tfaStatus.backup_codes_remaining} backup codes remaining
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {tfaStatus?.enabled ? (
                <>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowRegenerateBackup(true)}
                  >
                    Regenerate Backup Codes
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => setShowDisable2FA(true)}
                  >
                    Disable 2FA
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={() => {
                    setShowSetup2FA(true);
                    setSetupStep(1);
                  }}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  <Lock className="h-4 w-4 mr-2" />
                  Enable 2FA
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Privacy Controls */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-600" />
              AI Training & Privacy
            </CardTitle>
            <CardDescription>
              Control how your organization's data is used
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50">
              <div>
                <p className="font-medium">Opt-out of AI Training</p>
                <p className="text-sm text-slate-500">
                  When enabled, your organization's data will not be used for AI model training
                </p>
              </div>
              <Switch
                checked={aiConsent?.ai_training_opt_out || false}
                onCheckedChange={handleAIConsentToggle}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Export */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5 text-blue-600" />
            Data Export
          </CardTitle>
          <CardDescription>
            Export your data for backup or compliance purposes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg border dark:border-slate-700">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-slate-500" />
                <span className="font-medium">My Personal Data</span>
              </div>
              <p className="text-sm text-slate-500 mb-3">
                Export all your personal data (GDPR compliant)
              </p>
              <Button variant="outline" size="sm" onClick={handleExportMyData}>
                <FileJson className="h-4 w-4 mr-2" />
                Export as JSON
              </Button>
            </div>
            
            {isAdmin && (
              <div className="p-4 rounded-lg border dark:border-slate-700">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-slate-500" />
                  <span className="font-medium">Audit Logs</span>
                </div>
                <p className="text-sm text-slate-500 mb-3">
                  Export system audit logs
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleExportAuditLogs('csv')}>
                    Export CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleExportAuditLogs('json')}>
                    Export JSON
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Compliance Report */}
      <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-emerald-600" />
                  Security Compliance Report
                </CardTitle>
                <CardDescription>
                  Overview of implemented security controls
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const url = `${API_URL}/api/security/compliance-report/download?format=html`;
                  window.open(url, '_blank');
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Download Report
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {!complianceReport ? (
              <div className="flex items-center justify-center p-8 text-slate-500">
                <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                Loading compliance data...
              </div>
            ) : (
              <>
            {/* Encryption Section */}
            <Collapsible open={expandedSections.encryption} onOpenChange={() => toggleSection('encryption')}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800">
                <div className="flex items-center gap-2">
                  <Lock className="h-4 w-4 text-teal-600" />
                  <span className="font-medium">Encryption</span>
                </div>
                {expandedSections.encryption ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-6">
                  <div className="p-3 rounded border dark:border-slate-700">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">At Rest</span>
                      {getStatusBadge(complianceReport?.security_controls?.encryption?.at_rest?.status)}
                    </div>
                    <p className="text-xs text-slate-500">
                      {complianceReport?.security_controls?.encryption?.at_rest?.algorithm || 'Not configured'}
                    </p>
                  </div>
                  <div className="p-3 rounded border dark:border-slate-700">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">In Transit</span>
                      {getStatusBadge(complianceReport.security_controls?.encryption?.in_transit?.status)}
                    </div>
                    <p className="text-xs text-slate-500">
                      {complianceReport.security_controls?.encryption?.in_transit?.protocol}
                    </p>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Authentication Section */}
            <Collapsible open={expandedSections.authentication} onOpenChange={() => toggleSection('authentication')}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800">
                <div className="flex items-center gap-2">
                  <Key className="h-4 w-4 text-blue-600" />
                  <span className="font-medium">Authentication</span>
                </div>
                {expandedSections.authentication ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pl-6">
                  <div className="p-3 rounded border dark:border-slate-700">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">Password Hashing</span>
                      {getStatusBadge('ENABLED')}
                    </div>
                    <p className="text-xs text-slate-500">bcrypt</p>
                  </div>
                  <div className="p-3 rounded border dark:border-slate-700">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">2FA</span>
                      {getStatusBadge('AVAILABLE')}
                    </div>
                    <p className="text-xs text-slate-500">
                      {complianceReport.security_controls?.authentication?.two_factor_auth?.adoption_rate} adoption
                    </p>
                  </div>
                  <div className="p-3 rounded border dark:border-slate-700">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">Session Management</span>
                      {getStatusBadge('ENABLED')}
                    </div>
                    <p className="text-xs text-slate-500">JWT with expiry</p>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Access Control Section */}
            <Collapsible open={expandedSections.accessControl} onOpenChange={() => toggleSection('accessControl')}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800">
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-purple-600" />
                  <span className="font-medium">Access Control</span>
                </div>
                {expandedSections.accessControl ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-6">
                  <div className="p-3 rounded border dark:border-slate-700">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">RBAC</span>
                      {getStatusBadge('ENABLED')}
                    </div>
                    <p className="text-xs text-slate-500">Role-based access with feature permissions</p>
                  </div>
                  <div className="p-3 rounded border dark:border-slate-700">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">Tenant Isolation</span>
                      {getStatusBadge('ENABLED')}
                    </div>
                    <p className="text-xs text-slate-500">Complete data isolation between organizations</p>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Compliance Frameworks */}
            <div className="mt-6 pt-4 border-t dark:border-slate-700">
              <h4 className="text-sm font-semibold mb-3">Compliance Framework Alignment</h4>
              <p className="text-xs text-slate-500 mb-3">
                Technical controls are implemented. Full certification requires independent audit and organizational policies.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className="h-4 w-4 text-emerald-600" />
                    <span className="font-medium text-emerald-800 dark:text-emerald-400">SOC 2</span>
                  </div>
                  <p className="text-xs text-emerald-700 dark:text-emerald-500">Controls Implemented</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-blue-800 dark:text-blue-400">GDPR</span>
                  </div>
                  <p className="text-xs text-blue-700 dark:text-blue-500">Technical Controls Implemented</p>
                </div>
                <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className="h-4 w-4 text-purple-600" />
                    <span className="font-medium text-purple-800 dark:text-purple-400">HIPAA</span>
                  </div>
                  <p className="text-xs text-purple-700 dark:text-purple-500">Technical Safeguards Implemented</p>
                </div>
              </div>
            </div>

            {/* Recommendations */}
            {complianceReport.recommendations?.length > 0 && (
              <div className="mt-4 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-400 mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Recommendations
                </h4>
                <ul className="space-y-2">
                  {complianceReport?.recommendations?.map((rec, idx) => (
                    <li key={idx} className="text-sm text-amber-700 dark:text-amber-500">
                      <span className="font-medium">[{rec.priority}]</span> {rec.recommendation}
                    </li>
                  ))}
                </ul>
              </div>
            )}
              </>
            )}
          </CardContent>
        </Card>

      {/* 2FA Setup Dialog */}
      <Dialog open={showSetup2FA} onOpenChange={setShowSetup2FA}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-teal-600" />
              {setupStep === 1 && 'Enable Two-Factor Authentication'}
              {setupStep === 2 && 'Scan QR Code'}
              {setupStep === 3 && 'Save Backup Codes'}
            </DialogTitle>
            <DialogDescription>
              {setupStep === 1 && 'Enter your password to begin 2FA setup'}
              {setupStep === 2 && 'Scan this QR code with your authenticator app'}
              {setupStep === 3 && 'Save these backup codes in a secure location'}
            </DialogDescription>
          </DialogHeader>
          
          {setupStep === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSetup2FA(false)}>Cancel</Button>
                <Button onClick={handleSetup2FA} disabled={!password}>Continue</Button>
              </DialogFooter>
            </div>
          )}
          
          {setupStep === 2 && setupData && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <img src={setupData.qr_code} alt="2FA QR Code" className="w-48 h-48" />
              </div>
              <div className="text-center">
                <p className="text-xs text-slate-500 mb-2">Or enter this code manually:</p>
                <code className="px-3 py-1 rounded bg-slate-100 dark:bg-slate-800 text-sm font-mono">
                  {setupData.secret}
                </code>
              </div>
              <div>
                <Label htmlFor="verify-code">Verification Code</Label>
                <Input
                  id="verify-code"
                  type="text"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSetupStep(1)}>Back</Button>
                <Button onClick={handleVerify2FA} disabled={verifyCode.length !== 6}>Verify & Enable</Button>
              </DialogFooter>
            </div>
          )}
          
          {setupStep === 3 && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-800 dark:text-amber-400 mb-3">
                  Save these backup codes somewhere safe. You can use them to access your account if you lose your authenticator.
                </p>
                <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                  {backupCodes.map((code, idx) => (
                    <div key={idx} className="px-2 py-1 rounded bg-white dark:bg-slate-800">
                      {code}
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={copyBackupCodes}>
                  {copiedCode ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                  {copiedCode ? 'Copied!' : 'Copy Codes'}
                </Button>
                <Button onClick={() => setShowSetup2FA(false)}>Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={showDisable2FA} onOpenChange={setShowDisable2FA}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Enter your password and a 2FA code to disable two-factor authentication.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="disable-password">Password</Label>
              <Input
                id="disable-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
              />
            </div>
            <div>
              <Label htmlFor="disable-code">2FA Code</Label>
              <Input
                id="disable-code"
                type="text"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                maxLength={6}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisable2FA(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDisable2FA} disabled={!password || verifyCode.length !== 6}>
              Disable 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate Backup Codes Dialog */}
      <Dialog open={showRegenerateBackup} onOpenChange={setShowRegenerateBackup}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate Backup Codes</DialogTitle>
            <DialogDescription>
              Enter a 2FA code to generate new backup codes. Your old codes will be invalidated.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="regen-code">2FA Code</Label>
            <Input
              id="regen-code"
              type="text"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit code"
              maxLength={6}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegenerateBackup(false)}>Cancel</Button>
            <Button onClick={handleRegenerateBackup} disabled={verifyCode.length !== 6}>
              Regenerate Codes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onOpenChange={setShowBackupCodes}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Backup Codes</DialogTitle>
            <DialogDescription>
              Save these codes in a secure location. Your previous codes are now invalid.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm p-4 bg-slate-50 dark:bg-slate-800 rounded">
            {backupCodes.map((code, idx) => (
              <div key={idx} className="px-2 py-1">{code}</div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={copyBackupCodes}>
              {copiedCode ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
              Copy
            </Button>
            <Button onClick={() => setShowBackupCodes(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
